import React, { useCallback, useMemo, useState } from 'react';

import classNames from 'classnames';
import { useMutation, useQuery, useQueryCache } from 'react-query';
import { Toast } from 'src/common/components';
import { Icon } from 'src/common/components/Icon';
import { OrbitIcons } from 'src/common/constants.';

import {
  createDefaultUnavailableReasons,
  createUnavailableReason,
  deleteUnavailableReason,
  fetchUnavailableReasons,
  updateUnavailableReason,
} from '../api';
import type { UnavailableReason, UpdateUnavailableReasonPayload } from '../api';

interface UnavailableReasonsManagerProps {
  accountId: number;
}

interface ReasonFormState {
  name: string;
  position: number;
}

export const UnavailableReasonsManager: React.FC<UnavailableReasonsManagerProps> = ({
  accountId,
}) => {
  const queryCache = useQueryCache();
  const queryKey = useMemo(() => ['unavailable-reasons', accountId], [accountId]);
  const [editingReason, setEditingReason] = useState<UnavailableReason | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formState, setFormState] = useState<ReasonFormState>({ name: '', position: 0 });

  const {
    data: fetchedReasons,
    isLoading,
    isError,
  } = useQuery(queryKey, () => fetchUnavailableReasons(accountId), {
    refetchOnWindowFocus: false,
  });

  const reasons = useMemo(
    () => [...(fetchedReasons ?? [])].sort((a, b) => a.position - b.position || a.id - b.id),
    [fetchedReasons]
  );

  const resetForm = useCallback(() => {
    setFormState({ name: '', position: 0 });
    setEditingReason(null);
    setShowCreateForm(false);
  }, []);

  const [createReason, { isLoading: isCreating }] = useMutation(
    (payload: ReasonFormState) =>
      createUnavailableReason(accountId, payload.name, payload.position),
    {
      onSuccess: () => {
        Toast.success('Motivo creado exitosamente');
        queryCache.invalidateQueries(queryKey);
        resetForm();
      },
      onError: () => {
        Toast.error('Error al crear el motivo');
      },
    }
  );

  const [updateReason, { isLoading: isUpdating }] = useMutation(
    ({ id, payload }: { id: number; payload: UpdateUnavailableReasonPayload }) =>
      updateUnavailableReason(id, payload),
    {
      onSuccess: () => {
        Toast.success('Motivo actualizado exitosamente');
        queryCache.invalidateQueries(queryKey);
        resetForm();
      },
      onError: () => {
        Toast.error('Error al actualizar el motivo');
      },
    }
  );

  const [deactivateReason] = useMutation((id: number) => deleteUnavailableReason(id), {
    onSuccess: () => {
      Toast.success('Motivo desactivado exitosamente');
      queryCache.invalidateQueries(queryKey);
    },
    onError: () => {
      Toast.error('Error al desactivar el motivo');
    },
  });

  const [activateReason] = useMutation(
    (id: number) => updateUnavailableReason(id, { is_active: true }),
    {
      onSuccess: () => {
        Toast.success('Motivo activado exitosamente');
        queryCache.invalidateQueries(queryKey);
      },
      onError: () => {
        Toast.error('Error al activar el motivo');
      },
    }
  );

  const [loadDefaults, { isLoading: isLoadingDefaults }] = useMutation(
    () => createDefaultUnavailableReasons(accountId),
    {
      onSuccess: () => {
        Toast.success('Motivos por defecto cargados');
        queryCache.invalidateQueries(queryKey);
      },
      onError: () => {
        Toast.error('Error al cargar los motivos por defecto');
      },
    }
  );

  const handleCreate = useCallback(() => {
    const nextPosition = reasons.reduce((max, r) => Math.max(max, r.position), 0) + 1;
    setShowCreateForm(true);
    setEditingReason(null);
    setFormState({ name: '', position: nextPosition });
  }, [reasons]);

  const handleEdit = useCallback((reason: UnavailableReason) => {
    setEditingReason(reason);
    setShowCreateForm(false);
    setFormState({ name: reason.name, position: reason.position });
  }, []);

  const handleSave = useCallback(() => {
    const name = formState.name.trim();
    if (editingReason) {
      updateReason({ id: editingReason.id, payload: { name, position: formState.position } });
    } else {
      createReason({ name, position: formState.position });
    }
  }, [formState, editingReason, createReason, updateReason]);

  const isFormValid = formState.name.trim().length > 0 && Number.isInteger(formState.position);
  const isSaving = isCreating || isUpdating;
  const isFormOpen = showCreateForm || editingReason !== null;

  return (
    <div className="p-4">
      {isLoading && <p className="text-sm text-gray-2.0">Cargando motivos...</p>}

      {!isLoading && isError && (
        <p role="alert" className="text-sm text-red-500">
          Error al cargar los motivos
        </p>
      )}

      {!isLoading && !isError && !isFormOpen && (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-2.0">
              {reasons.length === 0
                ? 'No hay motivos configurados'
                : `${reasons.length} motivo(s) configurado(s)`}
            </p>
            <div className="flex gap-2">
              {reasons.length === 0 && (
                <button
                  type="button"
                  disabled={isLoadingDefaults}
                  onClick={() => loadDefaults()}
                  className="btn btn-ghost btn-sm"
                >
                  {isLoadingDefaults ? 'Cargando...' : 'Cargar motivos por defecto'}
                </button>
              )}
              <button
                type="button"
                onClick={handleCreate}
                className="btn btn-secondary btn-sm flex items-center gap-1"
              >
                <Icon name={OrbitIcons.PlusCircle} size={14} />
                Crear motivo
              </button>
            </div>
          </div>

          <div className="space-y-2">
            {reasons.map((reason) => (
              <div
                key={reason.id}
                className={classNames(
                  'flex items-center justify-between rounded border border-white-2.0 bg-white-1.0 px-3 py-2',
                  !reason.is_active && 'opacity-60'
                )}
              >
                <div>
                  <p className="text-sm font-semibold text-black-1.0">{reason.name}</p>
                  <p className="text-xs text-gray-2.0">
                    Orden {reason.position}
                    {!reason.is_active && ' · Inactivo'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    aria-label={`Editar ${reason.name}`}
                    onClick={() => handleEdit(reason)}
                    className="p-1 text-gray-2.0 hover:text-primary-2.0"
                  >
                    <Icon name={OrbitIcons.Pencil} size={14} />
                  </button>
                  {reason.is_active ? (
                    <button
                      type="button"
                      aria-label={`Desactivar ${reason.name}`}
                      onClick={() => {
                        if (window.confirm('¿Desactivar este motivo?')) {
                          deactivateReason(reason.id);
                        }
                      }}
                      className="p-1 text-gray-2.0 hover:text-red-500"
                    >
                      <Icon name={OrbitIcons.Trash} size={14} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      aria-label={`Activar ${reason.name}`}
                      onClick={() => activateReason(reason.id)}
                      className="p-1 text-gray-2.0 hover:text-primary-2.0"
                    >
                      <Icon name={OrbitIcons.Check} size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {isFormOpen && (
        <div className="rounded border border-white-2.0 bg-white-0.0 p-4">
          <h4 className="mb-4 text-sm font-bold text-black-1.0">
            {editingReason ? 'Editar motivo' : 'Nuevo motivo'}
          </h4>

          <div className="mb-4">
            <label
              htmlFor="unavailable-reason-name"
              className="mb-1 block text-xs font-medium text-gray-3.0"
            >
              Nombre del motivo<span className="ml-0.5 text-red-500">*</span>
            </label>
            <input
              id="unavailable-reason-name"
              type="text"
              placeholder="Ej: Almuerzo"
              value={formState.name}
              onChange={(e) => {
                const { value } = e.target;
                setFormState((prev) => ({ ...prev, name: value }));
              }}
              className="w-full rounded border border-white-2.0 px-3 py-2 text-sm"
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="unavailable-reason-position"
              className="mb-1 block text-xs font-medium text-gray-3.0"
            >
              Orden
            </label>
            <input
              id="unavailable-reason-position"
              type="number"
              min={0}
              value={formState.position}
              onChange={(e) => {
                const position = parseInt(e.target.value, 10);
                setFormState((prev) => ({ ...prev, position }));
              }}
              className="w-24 rounded border border-white-2.0 px-3 py-2 text-sm"
            />
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={resetForm} className="btn btn-ghost btn-sm">
              Cancelar
            </button>
            <button
              type="button"
              disabled={!isFormValid || isSaving}
              onClick={handleSave}
              className="btn btn-secondary btn-sm"
            >
              {isSaving ? 'Guardando...' : editingReason ? 'Guardar cambios' : 'Crear motivo'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
